/**
 * Posizione dell'icona flottante dell'assistente AI (spostabile col trascinamento).
 * Si salva come distanza dal bordo destro e da quello in basso, così resta "attaccata" all'angolo quando la
 * finestra cambia misura; viene sempre riportata dentro lo schermo.
 */

export interface PosizioneBolla {
  destra: number;
  basso: number;
}

export interface Finestra {
  larghezza: number;
  altezza: number;
}

export const LATO_BOLLA = 48;
export const MARGINE = 8;
export const POSIZIONE_PREDEFINITA: PosizioneBolla = { destra: 24, basso: 24 };
export const SOGLIA_TRASCINAMENTO = 5;
const CHIAVE = 'assistente-bolla-posizione';

export function limitaPosizione(p: PosizioneBolla, f: Finestra): PosizioneBolla {
  const maxDestra = Math.max(MARGINE, f.larghezza - LATO_BOLLA - MARGINE);
  const maxBasso = Math.max(MARGINE, f.altezza - LATO_BOLLA - MARGINE);
  return {
    destra: Math.round(Math.min(Math.max(p.destra, MARGINE), maxDestra)),
    basso: Math.round(Math.min(Math.max(p.basso, MARGINE), maxBasso)),
  };
}

/** Nuova posizione dopo uno spostamento del puntatore di (dx, dy) pixel. */
export function sposta(p: PosizioneBolla, dx: number, dy: number, f: Finestra): PosizioneBolla {
  return limitaPosizione({ destra: p.destra - dx, basso: p.basso - dy }, f);
}

export function eTrascinamento(dx: number, dy: number): boolean {
  return Math.abs(dx) > SOGLIA_TRASCINAMENTO || Math.abs(dy) > SOGLIA_TRASCINAMENTO;
}

export function leggiPosizione(): PosizioneBolla {
  try {
    const p = JSON.parse(localStorage.getItem(CHIAVE) || 'null');
    if (p && Number.isFinite(p.destra) && Number.isFinite(p.basso)) return { destra: p.destra, basso: p.basso };
  } catch {
    // archivio del browser non disponibile o valore rovinato: posizione predefinita
  }
  return POSIZIONE_PREDEFINITA;
}

export function salvaPosizione(p: PosizioneBolla): void {
  try {
    localStorage.setItem(CHIAVE, JSON.stringify(p));
  } catch {
    // archivio del browser non disponibile: la posizione vale fino al ricaricamento
  }
}

export function dimenticaPosizione(): void {
  try {
    localStorage.removeItem(CHIAVE);
  } catch {
    // niente da fare
  }
}

/**
 * Stile del pannello della chat: si apre verso il centro dello schermo rispetto all'icona (sopra se l'icona è
 * nella metà bassa, sotto se è in quella alta; allineato a destra o a sinistra allo stesso modo).
 */
export function stilePannello(p: PosizioneBolla, f: Finestra, larghezzaMax = 400, altezzaMax = 520): Record<string, number> {
  const larghezza = Math.min(larghezzaMax, f.larghezza - 2 * MARGINE);
  const sinistraBolla = f.larghezza - p.destra - LATO_BOLLA;
  const altoBolla = f.altezza - p.basso - LATO_BOLLA;
  const stile: Record<string, number> = { width: larghezza };

  if (sinistraBolla + LATO_BOLLA / 2 > f.larghezza / 2) {
    stile.right = Math.min(p.destra, f.larghezza - larghezza - MARGINE);
  } else {
    stile.left = Math.max(MARGINE, Math.min(sinistraBolla, f.larghezza - larghezza - MARGINE));
  }

  if (altoBolla + LATO_BOLLA / 2 > f.altezza / 2) {
    stile.bottom = p.basso + LATO_BOLLA + MARGINE;
    stile.height = Math.max(240, Math.min(altezzaMax, f.altezza - stile.bottom - MARGINE));
  } else {
    stile.top = altoBolla + LATO_BOLLA + MARGINE;
    stile.height = Math.max(240, Math.min(altezzaMax, f.altezza - stile.top - MARGINE));
  }
  return stile;
}
